import { useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";

import { Card, Checkbox, Flex } from "antd";

import styles from "./KanbanBoard.module.css";

import routes from "@/routes";

import { PRIORITY_OPTIONS, TASK_TYPE_OPTIONS } from "@/constants/tasks";

const getPriorityIcon = (color) => {
  return PRIORITY_OPTIONS.find((item) => item.value === color)?.icon;
};

const getTypeIcon = (key) => {
  return TASK_TYPE_OPTIONS.find((item) => item.value === key)?.icon;
};

/**
 * Компонент карточки задачи для Канбан доски
 * @param {Object} task - Объект задачи
 * @param {boolean} isDragging - Флаг перетаскивания
 * @param {Array} priorities - Массив приоритетов
 * @param {Array} taskTypes - Массив типов задач
 * @param {boolean} isAdmins - Флаг, является ли пользователь администратором
 * @param {string|null} unit - UUID РГ, если задача в контексте рг
 */
const KanbanCard = ({
  task,
  isDragging,
  priorities = [],
  taskTypes = [],
  isAdmins = false,
  unit = null,
  selectable = false,
  selected = false,
  onSelect,
}) => {
  const navigate = useNavigate();

  const priority = useMemo(() => {
    return priorities.find(
      (priority) => priority.uuid === task?.importance_uuid
    );
  }, [priorities, task]);

  const taskType = useMemo(() => {
    return taskTypes.find((type) => type.uuid === task?.task_type_uuid);
  }, [taskTypes, task]);

  const isSubTask = useMemo(() => {
    return task?.parent_task_uuid && task?.parent?.task_number;
  }, [task]);

  const taskHref = useMemo(() => {
    return isAdmins
      ? routes.administration.taskDetail(task.uuid)
      : unit
      ? routes.unitTaskDetail(task.unit_uuid, task.uuid)
      : routes.taskDetail(task.uuid);
  }, [task, unit, isAdmins]);

  return (
    <Card
      className={`${styles.kanbanCard} ${isDragging ? styles.isDragging : ""}`}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? "grabbing" : "pointer",
        boxShadow: isDragging ? "0 5px 15px rgba(0,0,0,0.3)" : undefined,
        transition: "all 0.2s",
      }}
      styles={{
        body: {
          padding: 15,
        },
      }}
      hoverable
      onClick={() => {
        navigate(taskHref);
      }}
    >
      <Flex vertical gap="small">
        <Flex align="center" justify="space-between" gap={10}>
          <Flex align="center" gap={10}>
            <Flex align="center" gap={5} className={styles.cardNumber}>
              {taskType && (
                <span style={{ fontSize: 16 }}>
                  {getTypeIcon(taskType.key)}
                </span>
              )}
              {isSubTask ? "Подзадача" : "Задача"} {task.task_number}
            </Flex>

            {priority && getPriorityIcon(priority.color)}
          </Flex>

          {selectable && (
            <Checkbox
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onSelect?.(task.uuid, e.target.checked)}
            />
          )}
        </Flex>
        <div className={styles.cardTitle}>{task?.name}</div>
        {/* Описание задачи */}
        {task?.description && (
          <div className={styles.cardDescription} title={task?.description}>
            {task.description}
          </div>
        )}
      </Flex>
    </Card>
  );
};

export default memo(KanbanCard);
